import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import {
  DeleteConfirmationModal,
  DeleteIcon,
  Drawer,
  Input,
  TextArea,
} from "components";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

interface FormData {
  title: string;
  note: string;
}

const schema = yup.object({
  title: yup.string().required("Title is required").max(26),
  note: yup.string().required("Note is required").max(1001),
});

export const AddNotesDrawer = ({
  open,
  onClose,
  data,
  editNoteDataState,
  addNote,
  updateNote,
  type,
  deleteNotes,
  isLoading,
  isDeleteNoteLoading,
}: {
  open: boolean;
  onClose: () => void;
  data?: any;
  editNoteDataState?: any;
  addNote: (data: any) => any;
  updateNote: (data: any) => any;
  type: "customer" | "user";
  deleteNotes: UseMutateAsyncFunction<any, unknown, string[], unknown>;
  isLoading: boolean;
  isDeleteNoteLoading: boolean;
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
    setValue,
  } = useForm({
    defaultValues: {
      title: data?.title || "",
      note: data?.note || "",
    },
    resolver: yupResolver(schema),
  });

  interface Params {
    id: string;
    [key: string]: string | undefined; // Add an index signature to satisfy the constraint
  }

  const params = useParams<Params>();
  const theme = useTheme();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [drawerText, setTitleText] = React.useState<{
    title: string;
    primaryButtonText: string;
  }>({
    title: "New Note",
    primaryButtonText: "Create",
  });
  /* api funcitons*/
  const { id = "" } = params;

  const deleteDocumentFunction = async () => {
    try {
      const noteIds = selectedData?.editData?.mapping_id;
      if (noteIds) {
        await deleteNotes([noteIds]);
        setDeleteConfirmationOpen(false);

        onClose();
        toast.success("Note deleted successfully");
      }
    } catch (error) {
      toast.error("Unable to delete document at the moment. Please try again");
    }
  };

  useEffect(() => {
    if (editNoteDataState?.isEdit) {
      setTitleText({ title: "Edit Note", primaryButtonText: "Update" });
      setValue("title", editNoteDataState?.editData?.title);
      setValue("note", editNoteDataState?.editData?.notes);
      setSelectedData(editNoteDataState);
    } else {
      setTitleText({ title: "New Note", primaryButtonText: "Create" });
      setValue("title", "");
      setValue("note", "");
    }
  }, [editNoteDataState]);

  async function createNote(noteData: any) {
    if (editNoteDataState?.isEdit) {
      try {
        await updateNote({
          noteId: editNoteDataState?.editData?.mapping_id,
          ...(type === "customer"
            ? {
                customerId: id,
              }
            : { customerUserId: id }),
          noteData,
        });
        toast.success("Note updated successfully");
        reset();
        onClose();
      } catch (error: any) {
        const errorMessage =
          error?.message || "Unable to update note. Please try again later.";
        toast.error(errorMessage);
      }
    } else {
      try {
        const createNote =
          type === "user"
            ? await addNote({ customerUserId: id, noteData })
            : await addNote({ customerId: id, noteData });
        if (createNote) {
          reset();
          toast.success("Note created successfully");
          reset();
          onClose();
        }
      } catch (error: any) {
        const errorMessage =
          error?.message || "Unable to create note. Please try again later.";
        toast.error(errorMessage);
      }
    }
  }

  return (
    <>
      <Drawer
        title={drawerText?.title}
        drawerStyle={{ width: "448px" }}
        open={open}
        onClose={() => {
          reset();
          onClose();
        }}
        onOpen={() => {}}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={onClose}
        primaryButtonText={drawerText?.primaryButtonText}
        onPrimaryButtonClick={handleSubmit((data) => createNote(data))}
        headerRightActionComponent={
          editNoteDataState?.isEdit && (
            <IconButton
              onClick={() => {
                setDeleteConfirmationOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )
        }
        isPrimaryButtonDisabled={!isDirty}
        isLoading={isLoading}
      >
        <Stack sx={{ padding: "26px", height: "400px", gap: "20px" }}>
          <Input
            name={"title"}
            required
            control={control}
            label="Title"
            fullWidth
            error={errors?.title ? true : false}
            errorMessage={errors?.title && "Title is required"}
            inputProps={{ maxLength: 25 }}
            // helperText={errors?.title && "Title is required"}
          />
          <Box sx={{ height: "100%", width: "100%" }}>
            {/* <Input
              multiline={true}
              name="note"
              rows={5}
              hideHelperBox={true}
              control={control}
              sx={{
                "& .MuiInputBase-root": {
                  flex: "100%",
                  alignItems: "start",
                },
                width: "100%",
              }}
              placeholder="Write your note...*"
              error={errors?.note ? true : false}
              //@ts-ignore
              errorMessage={errors?.note?.message}
            /> */}
            <TextArea
              name="note"
              minRows={16}
              maxLength={1000}
              error={errors?.note ? true : false}
              //@ts-ignore
              errorMessage={errors?.note?.message}
              placeholder="Write your note...*"
              control={control}
              style={{
                width: "100%",
                borderRadius: "4px",
                padding: "12px",
                resize: "none",
                height: "95%",
                ...theme.typography["body-medium"],
              }}
            />
          </Box>
        </Stack>
      </Drawer>
      <DeleteConfirmationModal
        open={deleteConfirmationOpen}
        title="Delete Note!!"
        tagline="Are you sure you want to delete ?"
        onClose={() => setDeleteConfirmationOpen(false)}
        onSecondaryButtonClick={() => setDeleteConfirmationOpen(false)}
        onPrimaryButtonClick={deleteDocumentFunction}
        isLoading={isDeleteNoteLoading}
      />
    </>
  );
};
